import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import {
  BuilderStatsPeriod,
  BuilderSortKey,
  BuilderStatus,
  BuilderStatusFilter,
  builderErrorMessage,
} from "../../../utils/builders";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface IBuilder {
  builder_id: string;
  name: string;
  fee_account: string;
  status: BuilderStatus;
}

export interface IBuildersResponse {
  builders: IBuilder[];
  count?: string;
  // null when the protocol cap is unset (all builder fees rejected).
  max_fee_rate_perps: string | null;
  // V2: null when self-service registration is disabled (absent on V1 backends).
  min_create_balance?: string | null;
  // Options builder fees: disabled (fail closed) until BOTH are set.
  max_fee_rate_options?: string | null;
  option_premium_cap?: string | null;
}

export interface ISetBuilderRequest {
  // Omit to create; the backend generates builder_[0-9a-f]{16}.
  builder_id?: string;
  name: string;
  fee_account: string;
  status: BuilderStatus;
}

export interface IBuilderMutationResult {
  ok: boolean;
  // builder_id confirmed by the backend (POST /builders only).
  builderId?: string;
  error?: string;
}

export interface IBuildersQueryParams {
  limit?: number;
  offset?: number;
  search?: string;
  status?: BuilderStatusFilter;
  sort?: BuilderSortKey;
}

export const builderRequestError = (error: unknown): string | undefined => {
  const payload = (error as AxiosError<{ error?: string; message?: string }>)
    ?.response?.data;
  return payload?.error || payload?.message ||
    (error instanceof Error ? error.message : undefined);
};

export interface IBuilderStatsTotals {
  volume: string;
  fees: string;
  unique_traders: number;
  fills: number;
}

export interface IBuilderStatsMarket {
  instrument: string;
  volume: string;
  fees: string;
  fills: number;
}

// Shape shared with GET /builder/stats (Builder Codes V2 contract).
export interface IBuilderStats extends IBuilderStatsTotals {
  builder_id: string;
  name: string;
  fee_account: string;
  status: BuilderStatus;
  period: string;
  start_time: string;
  end_time: string;
  active_traders: number;
  new_traders: number;
  returning_traders: number;
  maker_volume: string;
  taker_volume: string;
  previous?: IBuilderStatsTotals;
  markets?: IBuilderStatsMarket[];
}

export const builderStatsEndpoint = (builderId: string, period: BuilderStatsPeriod) =>
  ADMIN_API_URL(
    `${AdminEndpointEnum.BUILDERS}/${encodeURIComponent(builderId)}/stats?period=${period}`
  );

// GET /builders/:builder_id/stats (READ_SYSTEM)
export const useBuilderStats = (
  builderId: string | undefined,
  period: BuilderStatsPeriod
) => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const endpoint = builderId ? builderStatsEndpoint(builderId, period) : undefined;
  return useSWR<IBuilderStats, AxiosError>(
    tokenId && endpoint ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<IBuilderStats>(
        endpoint as string,
        queryConfig()
      );
      return response.data;
    }
  );
};

// useBuilders wraps the exchange-backend admin endpoints:
//   GET  /builders        (READ_SYSTEM)  list + protocol cap + min balance
//   POST /builders        (WRITE_SYSTEM) create / update builder
//   POST /builder-config  (WRITE_SYSTEM) set max_fee_rate_perps or
//                                        min_create_balance, max_fee_rate_options
//                                        or option_premium_cap (one field each)
export const useBuilders = ({
  limit,
  offset,
  search = "",
  status = "ALL",
  sort,
}: IBuildersQueryParams = {}) => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const endpoint = ADMIN_API_URL(AdminEndpointEnum.BUILDERS);
  const normalizedSearch = search.trim();
  const requestParams: Record<string, string | number> = {};
  if (limit !== undefined) requestParams.limit = limit;
  if (offset !== undefined) requestParams.offset = offset;
  if (normalizedSearch) requestParams.search = normalizedSearch;
  if (status !== "ALL") requestParams.status = status;
  if (sort) requestParams.sort = sort;

  const swr = useSWR<IBuildersResponse, AxiosError>(
    tokenId
      ? [endpoint, tokenId, limit, offset, normalizedSearch, status, sort]
      : undefined,
    async () => {
      const config = queryConfig();
      const response = await axios.get<IBuildersResponse>(
        endpoint,
        {
          ...config,
          params: {
            ...(config as { params?: Record<string, unknown> }).params,
            ...requestParams,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );
  const isStale = Boolean(swr.data && swr.isLoading);

  const notify = useCallback(
    (toastStatus: ToastStatusEnum, header: string, subheader?: string) => {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader,
          status: toastStatus,
        },
        6000
      );
    },
    [addToast]
  );

  const setBuilder = useCallback(
    async (request: ISetBuilderRequest): Promise<IBuilderMutationResult> => {
      const isCreate = !request.builder_id;
      try {
        const response = await axios.post<{ success?: boolean; builder_id?: string }>(
          ADMIN_API_URL(AdminEndpointEnum.BUILDERS),
          request,
          queryConfig()
        );
        if (response.data?.success !== true) {
          throw new Error("Backend did not confirm the builder update");
        }
        notify(
          ToastStatusEnum.SUCCESS,
          isCreate ? "Builder created" : "Builder updated",
          request.name
        );
        // Keep the list authoritative; a refresh failure is displayed as a read error.
        await swr.mutate().catch(() => undefined);
        return { ok: true, builderId: response.data.builder_id };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        const message = builderErrorMessage(builderRequestError(e));
        notify(
          ToastStatusEnum.ERROR,
          isCreate ? "Create builder failed" : "Update builder failed",
          message
        );
        return { ok: false, error: message };
      }
    },
    [notify, queryConfig, swr]
  );

  const setMaxFeeRatePerps = useCallback(
    async (maxFeeRatePerps: string): Promise<IBuilderMutationResult> => {
      try {
        const response = await axios.post<{ success?: boolean }>(
          ADMIN_API_URL(AdminEndpointEnum.BUILDER_CONFIG),
          { max_fee_rate_perps: maxFeeRatePerps },
          queryConfig()
        );
        if (response.data?.success !== true) {
          throw new Error("Backend did not confirm the builder fee cap update");
        }
        notify(
          ToastStatusEnum.SUCCESS,
          "Builder fee cap updated",
          maxFeeRatePerps
        );
        // Keep the list authoritative; a refresh failure is displayed as a read error.
        await swr.mutate().catch(() => undefined);
        return { ok: true };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        const message = builderErrorMessage(builderRequestError(e));
        notify(ToastStatusEnum.ERROR, "Set builder fee cap failed", message);
        return { ok: false, error: message };
      }
    },
    [notify, queryConfig, swr]
  );

  const setMinCreateBalance = useCallback(
    async (minCreateBalance: string): Promise<IBuilderMutationResult> => {
      try {
        // Send ONLY min_create_balance: absent fields are left unchanged, so
        // the protocol fee cap is untouched.
        const response = await axios.post<{ success?: boolean }>(
          ADMIN_API_URL(AdminEndpointEnum.BUILDER_CONFIG),
          { min_create_balance: minCreateBalance },
          queryConfig()
        );
        if (response.data?.success !== true) {
          throw new Error("Backend did not confirm the minimum balance update");
        }
        notify(
          ToastStatusEnum.SUCCESS,
          "Self-service minimum balance updated",
          `${minCreateBalance} USDC`
        );
        // Keep the list authoritative; a refresh failure is displayed as a read error.
        await swr.mutate().catch(() => undefined);
        return { ok: true };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        const message = builderErrorMessage(builderRequestError(e));
        notify(ToastStatusEnum.ERROR, "Set minimum balance failed", message);
        return { ok: false, error: message };
      }
    },
    [notify, queryConfig, swr]
  );

  // Posts exactly one builder-config field; absent fields stay unchanged.
  const setSingleConfigField = useCallback(
    async (
      field: "max_fee_rate_options" | "option_premium_cap",
      value: string,
      label: string
    ): Promise<IBuilderMutationResult> => {
      try {
        const response = await axios.post<{ success?: boolean }>(
          ADMIN_API_URL(AdminEndpointEnum.BUILDER_CONFIG),
          { [field]: value },
          queryConfig()
        );
        if (response.data?.success !== true) {
          throw new Error(`Backend did not confirm the ${label} update`);
        }
        notify(ToastStatusEnum.SUCCESS, `${label} updated`, value);
        // Keep the list authoritative; a refresh failure is displayed as a read error.
        await swr.mutate().catch(() => undefined);
        return { ok: true };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        const message = builderErrorMessage(builderRequestError(e));
        notify(ToastStatusEnum.ERROR, `Set ${label} failed`, message);
        return { ok: false, error: message };
      }
    },
    [notify, queryConfig, swr]
  );

  const setMaxFeeRateOptions = useCallback(
    (rate: string) => setSingleConfigField("max_fee_rate_options", rate, "Options builder fee cap"),
    [setSingleConfigField]
  );

  const setOptionPremiumCap = useCallback(
    (cap: string) => setSingleConfigField("option_premium_cap", cap, "Option premium cap"),
    [setSingleConfigField]
  );

  return {
    ...swr,
    isStale,
    setBuilder,
    setMaxFeeRatePerps,
    setMinCreateBalance,
    setMaxFeeRateOptions,
    setOptionPremiumCap,
  };
};
